import React, { useState, useEffect } from 'react';
import API from '../http/API';
import {useNavigate} from "react-router-dom";
import { useCourt, useRequestUrl, useForwardingOrg, useCaptchaKey } from '../context/contextHooks';
import { useStyles } from "../styles/styles";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery'
import { getSelectStates } from "./homeFunctions";
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HaveAgency from "./HaveAgency";
import Options from './Options';

const Home = () => {
    const classes = useStyles;
    const theme = useTheme();
    const smallHeader = useMediaQuery(theme.breakpoints.down('lg'));
    const navigate = useNavigate();
    const [requestUrl,] = useRequestUrl();
    const [forwardingOrg, setForwardingOrg] = useForwardingOrg();
    const [court, setCourt] = useCourt();
    const [captchaKey, setCaptchaKey] = useCaptchaKey();
    const [loading, setLoading] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [orgs, setOrgs] = useState(null);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [courtStates, setCourtStates] = useState(null);

    const clearMessaging = () => {
        setActionResponse(null);
    }

    useEffect(() => {
        document.title = 'Select Court - Neumo Payments';
    }, []);

    const getCourtStates = () => {
        clearMessaging();
        setLoading(true);
        const url = `${requestUrl}/getCourtStates`;
        API.getRetrieve(url, 'getCourtStates', null, setActionResponse);
    }

    const getOrgs = (state) => {
        clearMessaging();
        setSelectedOrg(null);
        // Always fetch: skipping when state matched the stored orgState emptied the
        // list on a return visit, and left loading stuck true with no request to
        // clear it (see handleState in Options).
        if (state) {
            setLoading(true);
            const url = `${requestUrl}/getOrgs/${state}`;
            API.getRetrieve(url, 'getOrgs', null, setActionResponse);
        }
    }

    const getCaptchaKey = () => {
        clearMessaging();
        setLoading(true);
        const url = `${requestUrl}/getCaptchaKey`;
        API.getRetrieve(url, 'getCaptchaKey', null, setActionResponse);
    }

    const showNext = (destination) => {
        if (destination) {
            if (destination.key === 'balances') {
                navigate('/balanceLookup');
            } else if (destination.key === 'miscFees') {
                navigate('/miscFees');
            }
        } else {
            setActionResponse({error: true, errorMessage: 'No payment type has been selected'});
        }
    }

    useEffect(() => {
        if (!loading && !courtStates) {
            getCourtStates();
        }
    }, [courtStates, loading])

    useEffect(() => {
        if (!loading && !captchaKey && (!actionResponse || !actionResponse.error)) {
            getCaptchaKey();
        }
    }, [captchaKey, loading])

    useEffect(() => {
        setCourt(selectedOrg);
    }, [selectedOrg])

    useEffect(() => {
        if (court && court.landingPageConfig && selectedOrg && !forwardingOrg) {
            const state = {court: court};
            navigate('/home', {state: state});
        }
    }, [court])

    useEffect(() => {
        if (loading && actionResponse) {
            if (!actionResponse.error) {
                if (actionResponse.requestType === 'getOrgs' && actionResponse.data) {
                    setOrgs(actionResponse.data);
                } else if (actionResponse.requestType === 'getCourtStates') {
                    setCourtStates(getSelectStates(actionResponse.data));
                } else if (actionResponse.requestType === 'getOrg') {
                    if (actionResponse.data) {
                        setSelectedOrg(actionResponse.data);
                        setForwardingOrg(actionResponse.data);
                    }
                } else if (actionResponse.requestType === 'getCaptchaKey') {
                    setCaptchaKey(actionResponse.data);
                }
            }
            setLoading(false);
        }
    }, [loading, actionResponse])

    return (
        <>
            <Typography variant={smallHeader ? 'h5' : 'h4'} component="h1" color='primary' sx={{...classes.title, marginTop: '1rem'}}>Welcome</Typography>
            <Grid
                container
                spacing={3}
                sx={{
                    justifyContent: "center"
                }}
            >
                <Box
                    component={Grid}
                    size={{xs: 12, sm: 6, md: 5}}
                    sx={{
                        justifyItems: 'center',
                        display: {xs: 'none', sm: 'block'}
                    }}>
                    {/* The intro is the list's label, not its first item, so a screen reader
                        announces "list, 3 items" with the intro as its name (CPDEV-2828).
                        The padding reproduces the offsets it had as a ListItem. */}
                    <Typography
                        id='payment-steps-intro'
                        variant={smallHeader ? 'subtitle2' : 'subtitle1'}
                        component='p'
                        sx={{
                            fontSize: smallHeader ? '' : '1.2rem',
                            lineHeight: smallHeader ? '1.2rem' : '1.4rem',
                            marginTop: `${smallHeader ? '0' : '1rem'}`,
                            padding: 'calc(8px + .3rem + 4px) 1.3rem calc(.3rem + 4px)'
                        }}
                    >
                        To make a payment to a court or government agency, follow these steps:
                    </Typography>
                    <List component='ol' aria-labelledby='payment-steps-intro' sx={{width: '100%', paddingTop: 0}} >
                        <ListItem alignItems='center' sx={classes.listPadding}>
                            <ListItemIcon sx={{padding: '0 1rem'}}>
                                <ChevronRightIcon color='secondary' sx={{fontSize: `${smallHeader ? '24pt' : '32pt'}`}} />
                            </ListItemIcon>
                            <ListItemText
                                sx={{padding: '0 1rem'}}
                                primary={
                                    <Typography
                                        variant={smallHeader ? 'subtitle2' : 'subtitle1'}
                                        component="span"
                                        sx={{fontSize: smallHeader ? '' : '1.2rem', lineHeight: smallHeader ? '1.2rem' : '1.4rem'}}
                                    >
                                        Enter the state and name of the organization you want to pay
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem alignItems='center' sx={classes.listPadding}>
                            <ListItemIcon sx={{padding: '0 1rem'}}>
                                <ChevronRightIcon color='secondary' sx={{fontSize: `${smallHeader ? '24pt' : '32pt'}`}} />
                            </ListItemIcon>
                            <ListItemText
                                sx={{padding: '0 1rem'}}
                                primary={
                                    <Typography
                                        variant={smallHeader ? 'subtitle2' : 'subtitle1'}
                                        component="span"
                                        sx={{fontSize: smallHeader ? '' : '1.2rem', lineHeight: smallHeader ? '1.2rem' : '1.4rem'}}
                                    >
                                        Select what you are paying for
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem alignItems='center' sx={classes.listPadding}>
                            <ListItemIcon sx={{padding: '0 1rem'}}>
                                <ChevronRightIcon color='secondary' sx={{fontSize: `${smallHeader ? '24pt' : '32pt'}`}} />
                            </ListItemIcon>
                            <ListItemText
                                sx={{padding: '0 1rem'}}
                                primary={
                                    <Typography
                                        variant={smallHeader ? 'subtitle2' : 'subtitle1'}
                                        component="span"
                                        sx={{fontSize: smallHeader ? '' : '1.2rem', lineHeight: smallHeader ? '1.2rem' : '1.4rem'}}
                                    >
                                        Enter the information requested to complete your payment
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                </Box>
                <Grid
                    sx={classes.gridBottom}
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 7
                    }}>
                    {actionResponse && actionResponse.error &&
                        <Alert severity='error'>
                            <AlertTitle>Error</AlertTitle>
                            {actionResponse.errorMessage}
                        </Alert>
                    }
                    { courtStates &&
                        <>
                            { (forwardingOrg) ?
                                <HaveAgency showNext={showNext} /> :
                                <Options
                                    courtStates={courtStates}
                                    getOrgs={getOrgs}
                                    orgs={orgs}
                                    court={court}
                                    setSelectedOrg={setSelectedOrg}
                                    showNext={showNext}
                                    setActionResponse={setActionResponse}
                                />
                            }
                        </>
                    }
                </Grid>
            </Grid>
        </>
    );
}

export default Home;
